/**
 * Undo/Redo action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 *
 * The undo/redo stacks are managed in the Zustand store. These hooks:
 * 1. Pop from the appropriate stack to get the change with patches to apply
 * 2. Dispatch the action with those patches
 * 3. The processUndoRedoInStore function (called after reducer) handles pushing
 *    the new inverse patches to the opposite stack
 */
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";

import type {DocumentStore, UndoRedoStore} from "../document";

/**
 * Hook to get the undoDocument action
 *
 * Pops from the undo stack (in the separate undoRedoStore) and dispatches
 * the action with the patches to apply. The processUndoRedoInStore function
 * handles pushing the new inverse patches to the redo stack.
 *
 * @returns A function to undo the last document change
 */
export function useUndoDocument(): () => void {
    const {documentStore, undoRedoStore} = useFluxServices();

    return useCallback(() => {
        const change = undoRedoStore.getState().popFromUndoStack();
        if (!change) {
            return;
        }
        // Dispatch with the patches to apply (inverse patches that undo the change)
        // processUndoRedoInStore will push the new inverse patches to the redo stack
        documentStore.getState().undoDocument(change.patchesForRedux);
    }, [documentStore, undoRedoStore]);
}

/**
 * Hook to get the redoDocument action
 *
 * Pops from the redo stack (in the separate undoRedoStore) and dispatches
 * the action with the patches to apply. The processUndoRedoInStore function
 * handles pushing the new inverse patches to the undo stack.
 *
 * @returns A function to redo the last undone document change
 */
export function useRedoDocument(): () => void {
    const {documentStore, undoRedoStore} = useFluxServices();

    return useCallback(() => {
        const change = undoRedoStore.getState().popFromRedoStack();
        if (!change) {
            return;
        }
        // Dispatch with the patches to apply (inverse patches that redo the change)
        // processUndoRedoInStore will push the new inverse patches to the undo stack
        documentStore.getState().redoDocument(change.patchesForRedux);
    }, [documentStore, undoRedoStore]);
}

/**
 * Factory function to create undo/redo actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch undo/redo actions.
 *
 * @param documentStore - The document store from FluxServices
 * @param undoRedoStore - The undo/redo store from FluxServices
 * @returns An object with undo/redo action methods
 */
export function createUndoRedoActions(documentStore: DocumentStore, undoRedoStore: UndoRedoStore) {
    return {
        undoDocument: () => {
            const change = undoRedoStore.getState().popFromUndoStack();
            if (!change) {
                return;
            }
            documentStore.getState().undoDocument(change.patchesForRedux);
        },
        redoDocument: () => {
            const change = undoRedoStore.getState().popFromRedoStack();
            if (!change) {
                return;
            }
            documentStore.getState().redoDocument(change.patchesForRedux);
        },
    };
}
